import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  CalendarIcon, 
  UsersIcon, 
  CurrencyDollarIcon, 
  TrophyIcon, 
  ArrowLeftIcon, 
  ClockIcon, 
  EyeIcon, 
  PencilIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import ChatPanel from '../components/ChatPanel'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface HorsePool {
  poolId: string
  name: string
  description: string
  type: 'horse-racing'
  status: 'active' | 'completed' | 'cancelled'
  settings: {
    track: string
    raceDate: string
    pickDeadline: string
    entryFee: number
    maxParticipants: number
  }
  currentParticipants: number
  createdAt: string
  createdBy: string
  races?: Race[]
  participants?: Participant[]
  userParticipation?: {
    isParticipant: boolean
    joinedAt?: string
    paid?: boolean
    completedPicks?: number
    totalPicks?: number
  }
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  entries: Horse[]
  scratches?: string[]
  status: 'upcoming' | 'running' | 'final'
  results?: {
    winner?: string
    place?: string
    show?: string
  }
}

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

interface Participant {
  userId: string
  name: string
  email: string
  joinedAt: string
  paid: boolean
}

export default function MemberHorseRacingPoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [pool, setPool] = useState<HorsePool | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [actualPickCount, setActualPickCount] = useState<number>(0)

  useEffect(() => {
    if (poolId) {
      fetchPool()
    }
  }, [poolId])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      setPool(data.pool)
      
      // Fetch user's actual pick count if they're a participant
      if (data.pool.userParticipation?.isParticipant) {
        try {
          const picksResponse = await fetch(`${API_BASE_URL}/pools/${poolId}/horse-picks`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (picksResponse.ok) {
            const picksData = await picksResponse.json()
            setActualPickCount(picksData.picks?.length || 0)
          }
        } catch (picksError) {
          console.log('Could not fetch pick count:', picksError)
        }
      }
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const joinPool = async () => {
    if (!poolId) return
    
    try {
      setJoining(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to join pool: ${response.statusText}`)
      }

      // Refresh pool data
      await fetchPool()
    } catch (err) {
      console.error('Error joining pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to join pool')
    } finally {
      setJoining(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Invalid Date'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getPickDeadline = () => {
    return pool?.settings?.pickDeadline || pool?.pickDeadline || (pool as any)?.pick_deadline
  }

  const getMaxParticipants = () => {
    return pool?.settings?.maxParticipants || (pool as any)?.maxParticipants || (pool as any)?.max_participants || 50
  }

  const getEntryFee = () => {
    return pool?.settings?.entryFee || (pool as any)?.entryFee || (pool as any)?.entry_fee || 0
  }

  const getCurrentParticipants = () => {
    return pool?.currentParticipants || (pool as any)?.current_participants || 0
  }

  const isDeadlinePassed = () => {
    const pickDeadline = getPickDeadline()
    if (!pickDeadline) return false
    
    const deadline = new Date(pickDeadline)
    if (isNaN(deadline.getTime())) return false
    
    return new Date() > deadline
  }
  const deadlinePassed = isDeadlinePassed()
  const maxParticipants = getMaxParticipants()
  const currentParticipants = getCurrentParticipants()
  const entryFee = getEntryFee()
  
  const canJoin = pool && pool.status === 'active' && 
                  currentParticipants < maxParticipants && 
                  !pool.userParticipation?.isParticipant &&
                  !deadlinePassed
  
  const canMakePicks = pool?.userParticipation?.isParticipant && !deadlinePassed

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Pool not found</h2>
          <p className="text-gray-600 dark:text-gray-300">The requested pool could not be found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-1" />
                Back to Pools
              </Link>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{pool.name}</h1>
            <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">{pool.description}</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(pool.status)}`}>
              {pool.status.charAt(0).toUpperCase() + pool.status.slice(1)}
            </span>
            <span className="text-2xl">🐎</span>
          </div>
        </div>
        <div className="mt-4 lg:hidden">
          <Link
            to={`/chat/pool/${poolId}`}
            state={{
              title: `${pool.name} chat`,
              subtitle: 'Pool participants only',
              locked: !pool.userParticipation?.isParticipant,
              lockedMessage: 'Join this pool to chat with other participants.',
            }}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
          >
            Open Pool Chat
          </Link>
        </div>
      </div>

      {/* Pool Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <CalendarIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Race Date</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatDate(pool.settings.raceDate)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{pool.settings.track}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <UsersIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Participants</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {currentParticipants} / {maxParticipants}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <CurrencyDollarIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Entry Fee</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">${entryFee}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <ClockIcon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Pick Deadline</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {formatDateTime(getPickDeadline() || '')}
              </p>
              {deadlinePassed && (
                <p className="text-xs text-red-600 dark:text-red-400">Deadline passed</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Status & Actions */}
      {pool.userParticipation?.isParticipant ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-green-900 dark:text-green-100">
                  You're participating in this pool!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Joined on {formatDate(pool.userParticipation.joinedAt!)} • 
                  Picks: {actualPickCount} / {pool.races?.length || 0}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              {canMakePicks && (
                <Link
                  to={`/pools/${poolId}/picks`}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Make Picks
                </Link>
              )}
              <Link
                to={`/pools/${poolId}/my-picks`}
                className="inline-flex items-center px-4 py-2 border border-green-600 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <EyeIcon className="w-4 h-4 mr-2" />
                View My Picks
              </Link>
              <Link
                to={`/pools/${poolId}/standings`}
                className="inline-flex items-center px-4 py-2 border border-green-600 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <TrophyIcon className="w-4 h-4 mr-2" />
                Standings
              </Link>
            </div>
          </div>
        </div>
      ) : canJoin ? (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">
                Join this horse racing pool
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Entry fee: ${entryFee} • 
                {maxParticipants - currentParticipants} spots remaining
              </p>
            </div>
            <button
              onClick={joinPool}
              disabled={joining}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Joining...
                </>
              ) : (
                <>
                  <TrophyIcon className="w-4 h-4 mr-2" />
                  Join Pool
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-8">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-gray-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {pool.status !== 'active' ? 'Pool is not active' : 
                 currentParticipants >= maxParticipants ? 'Pool is full' : 
                 deadlinePassed ? 'Deadline has passed' :
                 'Cannot join pool'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {pool.status !== 'active' ? 'This pool is no longer accepting participants.' :
                 currentParticipants >= maxParticipants ? 'All spots have been filled.' :
                 deadlinePassed ? 'The pick deadline has passed.' :
                 'You cannot join this pool at this time.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pool Actions - Match Football Pool Style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Make Picks */}
        <Link
          to={`/pools/${poolId}/picks`}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow ${
            !canMakePicks ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PencilIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Make Picks</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {pool.userParticipation?.isParticipant 
                  ? `${actualPickCount} of ${pool.races?.length || 0} picks made`
                  : 'Join pool to make picks'
                }
              </p>
            </div>
          </div>
        </Link>

        {/* View My Picks */}
        <Link
          to={`/pools/${poolId}/my-picks`}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow ${
            !pool.userParticipation?.isParticipant ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EyeIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">View My Picks</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                See your submitted picks and results
              </p>
            </div>
          </div>
        </Link>

        {/* Standings */}
        <Link
          to={`/pools/${poolId}/standings`}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrophyIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Standings</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                View leaderboard and rankings
              </p>
            </div>
          </div>
        </Link>
      </div>

      <div className="lg:grid lg:grid-cols-3 lg:gap-8">
      <div className="lg:col-span-2">
      {/* Race Information */}
      {pool.races && pool.races.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Race Card</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {pool.races.length} races • {pool.settings.track} • {formatDate(pool.settings.raceDate)}
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pool.races.map((race) => (
                <div
                  key={race.raceNumber}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Race {race.raceNumber}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {race.postTime}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {race.raceType} • {race.distance}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Purse: ${parseInt(race.purse).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {race.entries.length} entries
                    {race.scratches && race.scratches.length > 0 && (
                      <span className="text-red-600 dark:text-red-400 ml-2">
                        • {race.scratches.length} scratched
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Participants */}
      {pool.participants && pool.participants.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Participants</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pool.participants.map((participant) => (
                <div
                  key={participant.userId}
                  className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {participant.name}
                      {participant.userId === user?.sub && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(You)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Joined {formatDate(participant.joinedAt)}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {participant.paid ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <ClockIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>

      <div className="hidden lg:block lg:col-span-1">
        <ChatPanel
          kind="pool"
          entityId={poolId}
          title={`${pool.name} chat`}
          subtitle="Pool participants only"
          placeholder="Message this pool…"
          compact
          className="sticky top-24"
          locked={!pool.userParticipation?.isParticipant}
          lockedMessage="Join this pool to chat with other participants."
        />
      </div>
      </div>
    </div>
  )
}
